import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount } from "wagmi";
import {
  getOnboardingState,
  patchOnboardingState,
  restartOnboarding,
  OnboardingApiError,
  type OnboardingCanonical,
  type OnboardingGuidance,
  type OnboardingPatch,
} from "../../lib/onboarding-api";

/**
 * Macro #27 — authenticated onboarding guidance hook.
 *
 * A connected wallet alone is not sufficient: the guidance is scoped to the
 * SIWE session server-side, so an unauthenticated request surfaces as
 * `auth_required`. Stale-safe via a monotonic request id.
 */
export type OnboardingHookStatus = "idle" | "auth_required" | "loading" | "ready" | "error";

export interface UseOnboarding {
  status: OnboardingHookStatus;
  state: OnboardingGuidance | null;
  canonical: OnboardingCanonical | null;
  mutating: boolean;
  refresh: () => void;
  patch: (p: OnboardingPatch) => Promise<void>;
  restart: () => Promise<void>;
}

export function useOnboarding(): UseOnboarding {
  const { address, isConnected } = useAccount();
  const [status, setStatus] = useState<OnboardingHookStatus>("idle");
  const [state, setState] = useState<OnboardingGuidance | null>(null);
  const [canonical, setCanonical] = useState<OnboardingCanonical | null>(null);
  const [mutating, setMutating] = useState(false);
  const reqRef = useRef(0);

  const refresh = useCallback(() => {
    if (!isConnected || !address) {
      setStatus("auth_required");
      setState(null);
      setCanonical(null);
      return;
    }
    const id = ++reqRef.current;
    setStatus((s) => (s === "ready" ? s : "loading"));
    const controller = new AbortController();
    getOnboardingState(controller.signal)
      .then((res) => {
        if (id !== reqRef.current) return;
        setState(res.onboarding);
        setCanonical(res.canonical);
        setStatus("ready");
      })
      .catch((e: unknown) => {
        if (id !== reqRef.current) return;
        if ((e as { name?: string }).name === "AbortError") return;
        if (e instanceof OnboardingApiError && e.status === 401) {
          setStatus("auth_required");
          return;
        }
        setStatus("error");
      });
    return () => controller.abort();
  }, [address, isConnected]);

  useEffect(() => {
    reqRef.current++;
    setState(null);
    setCanonical(null);
    refresh();
    return () => {
      reqRef.current++;
    };
  }, [refresh]);

  const patch = useCallback(async (p: OnboardingPatch) => {
    const id = ++reqRef.current;
    setMutating(true);
    try {
      const res = await patchOnboardingState(p);
      if (id !== reqRef.current) return;
      setState(res.onboarding);
      setCanonical(res.canonical);
      setStatus("ready");
    } catch (e) {
      if (e instanceof OnboardingApiError && e.status === 401) setStatus("auth_required");
      throw e;
    } finally {
      setMutating(false);
    }
  }, []);

  const restart = useCallback(async () => {
    const id = ++reqRef.current;
    setMutating(true);
    try {
      const res = await restartOnboarding();
      if (id !== reqRef.current) return;
      setState(res.onboarding);
      setCanonical(res.canonical);
      setStatus("ready");
    } finally {
      setMutating(false);
    }
  }, []);

  return { status, state, canonical, mutating, refresh, patch, restart };
}
